<template>
  <div class="flex items-center justify-center p-0 w-full"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop">
    
    <!-- Drag Overlay -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDragging"
        class="fixed inset-0 z-[100] bg-[#2EB2A0]/95 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="bg-white/20 p-8 rounded-full mb-8 backdrop-blur-sm shadow-2xl">
           <span class="material-symbols-outlined text-white text-[100px]">call_split</span>
        </div>
        <h2 class="text-white text-5xl font-bold tracking-tight drop-shadow-sm">Drop PDF file to Split</h2>
      </div>
    </transition>

    <!-- Dropzone (Initial State) -->
    <div 
      v-if="pages.length === 0" 
      @click="fileInput.click()" 
      class="w-full max-w-[1000px] mt-4 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center text-center relative group p-0"
    >
      <div class="absolute -inset-1 bg-gradient-to-r from-[#2EB2A0] to-[#2EB2A0]/60 rounded-2xl blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
      <div class="relative w-full h-[360px] bg-white border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center gap-6 transition-all hover:border-[#2EB2A0]/50 p-6">
        <div class="size-20 bg-[#2EB2A0]/10 rounded-full flex items-center justify-center text-[#2EB2A0] mt-2">
           <span class="material-symbols-outlined text-[48px]">call_split</span>
        </div>
        
        <div class="space-y-1">
            <h3 class="text-2xl font-bold text-[#16304D]">Select or Drop PDF File to Split</h3>
            <p class="text-sm font-medium text-slate-500">Split PDF by ranges or extract all pages into separate documents</p>
        </div>

        <button @click.stop="fileInput.click()" class="bg-[#2EB2A0] text-white text-base font-semibold px-8 py-3 rounded-xl hover:bg-[#279B8C] shadow-xl shadow-[#2EB2A0]/20 transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center gap-2">
            <span class="material-symbols-outlined">add_circle</span>
            <span>Select PDF File</span>
        </button>

        <div class="flex flex-wrap justify-center items-center gap-4 text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1">
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">lock</span> Encrypted &amp; Secure</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">tune</span> Custom &amp; Fixed Ranges</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">folder_zip</span> ZIP or Merged Output</span>
        </div>
      </div>
    </div>

    <!-- Main Workspace (iLovePDF Style) -->
    <div v-else class="fixed inset-0 top-0 z-50 flex flex-col h-screen bg-[#F3F4F8]">
        <!-- Top Navigation Bar -->
        <header class="flex items-center justify-between whitespace-nowrap border-b border-slate-200 bg-white px-4 md:px-6 py-3 shrink-0 shadow-sm z-20">
            <div class="flex items-center gap-3 md:gap-4">
                <a href="/" class="flex items-center gap-2">
                     <img :src="'/logo.png'" alt="Logo" class="h-8 md:h-10 w-auto" @error="(e) => e.target.style.display='none'">
                     <span class="font-bold text-[#16304D] text-lg hidden sm:inline">Split PDF</span>
                </a>
                <div class="h-6 w-px bg-slate-200 hidden sm:block"></div>
                <div>
                    <div class="flex items-center gap-2 text-xs md:text-sm text-slate-600 font-medium">
                        <span class="font-bold text-[#16304D] truncate max-w-[220px]">{{ currentFileName }}</span>
                        <span class="text-slate-300">&middot;</span>
                        <span class="font-bold text-[#2EB2A0]">{{ pages.length }} Total Pages</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 md:gap-4">
                <button @click="resetAll" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-red-50 border border-red-200 text-sm font-semibold hover:bg-red-100 text-red-600 transition-colors">
                    <span class="material-symbols-outlined text-lg">restart_alt</span>
                    <span class="hidden md:inline">Start Over</span>
                </button>
            </div>
        </header>

        <div class="flex flex-1 overflow-hidden relative">
            <!-- Left/Center Main Canvas (Ranges Display) -->
            <main class="flex-1 overflow-y-auto bg-[#F3F4F8] p-4 md:p-8 relative">
                <div class="max-w-6xl mx-auto pb-24 space-y-8">
                    
                    <!-- TAB 1: RANGE MODE WORKSPACE -->
                    <div v-if="mainTab === 'range'" class="space-y-8">
                         <!-- Displaying Computed Ranges -->
                         <div class="flex flex-wrap items-stretch justify-center gap-8">
                              <div 
                                v-for="(range, rIdx) in computedRanges" 
                                :key="'range-' + rIdx"
                                class="flex flex-col items-center"
                              >
                                   <div class="text-slate-500 font-bold text-sm mb-3 flex items-center gap-2">
                                        <span>Range {{ rIdx + 1 }}</span>
                                        <button v-if="rangeMode === 'custom' && computedRanges.length > 1" @click="removeCustomRange(rIdx)" class="text-red-400 hover:text-red-600 text-xs">
                                            <span class="material-symbols-outlined text-base">cancel</span>
                                        </button>
                                   </div>

                                   <!-- Range Boundary Box -->
                                   <div class="p-4 bg-white/60 border-2 border-dashed border-slate-300 rounded-2xl flex flex-wrap gap-4 items-center justify-center min-w-[200px] min-h-[260px] shadow-sm">
                                        <div 
                                          v-for="pageObj in range.pages" 
                                          :key="'p-' + pageObj.pageNum" 
                                          class="flex flex-col items-center"
                                        >
                                             <div class="w-36 h-48 bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden relative flex items-center justify-center p-2">
                                                  <img 
                                                     v-if="pageObj.thumbnail"
                                                     :src="pageObj.thumbnail"
                                                     class="max-h-full max-w-full object-contain"
                                                  />
                                                  <div v-else class="animate-spin rounded-full h-6 w-6 border-2 border-[#2EB2A0]/30 border-t-[#2EB2A0]"></div>
                                             </div>
                                             <span class="text-xs font-bold text-slate-500 mt-2">{{ pageObj.pageNum }}</span>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <!-- TAB 2: EXTRACT PAGES WORKSPACE -->
                    <div v-else-if="mainTab === 'pages'" class="space-y-6">
                         <div class="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center text-xs font-bold">
                              <span class="text-slate-600">Select pages to extract:</span>
                              <div class="flex gap-2">
                                  <button @click="selectAllPages" class="text-[#2EB2A0] hover:underline">Select All</button>
                                  <span class="text-slate-300">&middot;</span>
                                  <button @click="deselectAllPages" class="text-slate-400 hover:underline">Deselect All</button>
                              </div>
                         </div>

                         <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                              <div 
                                v-for="page in pages" 
                                :key="'extract-' + page.pageNum"
                                @click="page.selected = !page.selected"
                                class="cursor-pointer group select-none"
                              >
                                   <div 
                                     class="h-52 bg-white rounded-xl border-2 relative transition-all overflow-hidden p-2 flex items-center justify-center"
                                     :class="page.selected ? 'border-[#2EB2A0] ring-4 ring-[#2EB2A0]/20 shadow-md' : 'border-slate-200 opacity-60 hover:opacity-100'"
                                   >
                                        <div 
                                          class="absolute top-2 right-2 size-6 rounded-full flex items-center justify-center shadow z-20 text-xs font-bold"
                                          :class="page.selected ? 'bg-[#2EB2A0] text-white' : 'bg-slate-200 text-slate-400'"
                                        >
                                            <span class="material-symbols-outlined text-sm">check</span>
                                        </div>

                                        <img v-if="page.thumbnail" :src="page.thumbnail" class="max-h-full max-w-full object-contain" />
                                        <div v-else class="animate-spin rounded-full h-6 w-6 border-2 border-[#2EB2A0]/30 border-t-[#2EB2A0]"></div>

                                        <div class="absolute bottom-0 left-0 right-0 bg-white/90 text-center py-1 border-t border-slate-100 text-xs font-bold text-slate-700">
                                            Page {{ page.pageNum }}
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                </div>
            </main>

            <!-- Right Options Sidebar (iLovePDF UI) -->
            <aside class="w-96 border-l border-slate-200 bg-white flex flex-col shrink-0 z-10 shadow-xl">
                <div class="p-6 flex flex-col flex-1 gap-6 overflow-y-auto">
                    
                    <h2 class="text-2xl font-extrabold text-[#16304D] text-center mb-1">Split</h2>

                    <!-- Main Tabs (Range vs Pages) -->
                    <div class="grid grid-cols-2 gap-2 bg-slate-100 p-1.5 rounded-xl border border-slate-200 text-center font-bold text-sm">
                         <button 
                           @click="mainTab = 'range'"
                           class="py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
                           :class="mainTab === 'range' ? 'bg-white text-[#16304D] shadow-sm' : 'text-slate-500 hover:text-slate-800'"
                         >
                              <span class="material-symbols-outlined text-lg">crop</span>
                              <span>Range</span>
                         </button>

                         <button 
                           @click="mainTab = 'pages'"
                           class="py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
                           :class="mainTab === 'pages' ? 'bg-white text-[#16304D] shadow-sm' : 'text-slate-500 hover:text-slate-800'"
                         >
                              <span class="material-symbols-outlined text-lg">grid_view</span>
                              <span>Pages</span>
                         </button>
                    </div>

                    <!-- TAB 1: RANGE SETTINGS -->
                    <div v-if="mainTab === 'range'" class="space-y-6">
                         <div>
                              <label class="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">Range mode:</label>
                              <div class="grid grid-cols-2 gap-2">
                                   <button 
                                     @click="rangeMode = 'custom'"
                                     class="py-2 px-3 rounded-xl border-2 font-bold text-xs transition-all"
                                     :class="rangeMode === 'custom' ? 'border-[#2EB2A0] bg-[#2EB2A0]/10 text-[#2EB2A0]' : 'border-slate-200 text-slate-600 hover:border-slate-300'"
                                   >
                                       Custom
                                   </button>
                                   <button 
                                     @click="rangeMode = 'fixed'"
                                     class="py-2 px-3 rounded-xl border-2 font-bold text-xs transition-all"
                                     :class="rangeMode === 'fixed' ? 'border-[#2EB2A0] bg-[#2EB2A0]/10 text-[#2EB2A0]' : 'border-slate-200 text-slate-600 hover:border-slate-300'"
                                   >
                                       Fixed
                                   </button>
                              </div>
                         </div>

                         <!-- CUSTOM RANGE CONTROLS -->
                         <div v-if="rangeMode === 'custom'" class="space-y-4">
                              <div 
                                v-for="(cr, cIdx) in customRanges" 
                                :key="'cr-' + cIdx"
                                class="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 relative"
                              >
                                   <div class="flex justify-between items-center text-xs font-bold text-[#16304D]">
                                        <span>Range {{ cIdx + 1 }}</span>
                                        <button v-if="customRanges.length > 1" @click="removeCustomRange(cIdx)" class="text-red-500 hover:underline text-[11px]">Remove</button>
                                   </div>
                                   <div class="grid grid-cols-2 gap-3">
                                        <div>
                                             <label class="text-[10px] font-bold text-slate-400 uppercase block mb-1">From page:</label>
                                             <input type="number" min="1" :max="pages.length" v-model.number="cr.from" class="w-full px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-bold text-[#16304D] focus:border-[#2EB2A0] outline-none" />
                                        </div>
                                        <div>
                                             <label class="text-[10px] font-bold text-slate-400 uppercase block mb-1">To page:</label>
                                             <input type="number" min="1" :max="pages.length" v-model.number="cr.to" class="w-full px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-bold text-[#16304D] focus:border-[#2EB2A0] outline-none" />
                                        </div>
                                   </div>
                              </div>

                              <button @click="addCustomRange" class="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 font-bold text-xs hover:border-[#2EB2A0] hover:text-[#2EB2A0] transition-colors flex items-center justify-center gap-1">
                                  <span class="material-symbols-outlined text-base">add</span>
                                  <span>Add Range</span>
                              </button>
                         </div>

                         <!-- FIXED RANGE CONTROLS -->
                         <div v-else-if="rangeMode === 'fixed'" class="space-y-4">
                              <div class="flex justify-between items-center bg-slate-50 p-4 rounded-xl border border-slate-200">
                                   <span class="text-xs font-bold text-slate-700">Split into page ranges of:</span>
                                   <input type="number" min="1" :max="pages.length" v-model.number="fixedPageSize" class="w-20 px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-bold text-center text-[#16304D] outline-none focus:border-[#2EB2A0]" />
                              </div>

                              <!-- Informational Box -->
                              <div class="bg-blue-50 border border-blue-200 p-4 rounded-xl flex items-start gap-3 text-xs text-blue-800">
                                   <span class="material-symbols-outlined text-lg text-blue-500 mt-0.5">info</span>
                                   <div>
                                        This PDF will be split into files of <strong>{{ fixedPageSize }}</strong> pages.<br/>
                                        <strong>{{ computedRanges.length }} PDFs</strong> will be created.
                                   </div>
                              </div>
                         </div>

                         <!-- Merge All Ranges Checkbox Option -->
                         <label class="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer">
                              <input type="checkbox" v-model="mergeAllRanges" class="size-4 accent-[#2EB2A0] rounded" />
                              <span class="text-xs font-bold text-slate-700">Merge all ranges in one PDF file</span>
                         </label>
                    </div>

                    <!-- TAB 2: EXTRACT PAGES SETTINGS -->
                    <div v-else-if="mainTab === 'pages'" class="space-y-6">
                         <div class="space-y-3">
                              <label 
                                @click="extractMode = 'selected'"
                                class="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
                                :class="extractMode === 'selected' ? 'border-[#2EB2A0] bg-[#2EB2A0]/5' : 'border-slate-200'"
                              >
                                   <input type="radio" value="selected" v-model="extractMode" class="accent-[#2EB2A0]" />
                                   <div>
                                        <h4 class="font-bold text-xs text-[#16304D]">Extract selected pages</h4>
                                        <p class="text-[11px] text-slate-500 mt-0.5">Extract only checked pages</p>
                                   </div>
                              </label>

                              <label 
                                @click="extractMode = 'all'"
                                class="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
                                :class="extractMode === 'all' ? 'border-[#2EB2A0] bg-[#2EB2A0]/5' : 'border-slate-200'"
                              >
                                   <input type="radio" value="all" v-model="extractMode" class="accent-[#2EB2A0]" />
                                   <div>
                                        <h4 class="font-bold text-xs text-[#16304D]">Extract all pages</h4>
                                        <p class="text-[11px] text-slate-500 mt-0.5">Separate every page into its own PDF</p>
                                   </div>
                              </label>
                         </div>

                         <div class="bg-blue-50 border border-blue-200 p-4 rounded-xl text-xs text-blue-800 flex items-start gap-2">
                              <span class="material-symbols-outlined text-base text-blue-500">info</span>
                              <span v-if="extractMode === 'selected'">Selected <strong>{{ selectedPageNumbers.length }}</strong> pages will be extracted.</span>
                              <span v-else>All <strong>{{ pages.length }}</strong> pages will be saved into a downloadable ZIP archive.</span>
                         </div>
                    </div>

                </div>

                <!-- Bottom Split PDF Action Button -->
                <div class="p-6 bg-slate-50 border-t border-slate-200">
                    <button 
                      @click="processSplit" 
                      :disabled="isProcessing || (mainTab === 'pages' && extractMode === 'selected' && selectedPageNumbers.length === 0)"
                      class="w-full bg-[#2EB2A0] text-white h-14 rounded-2xl text-lg font-bold flex items-center justify-center gap-3 shadow-lg hover:bg-[#279B8C] transition-all disabled:opacity-50"
                    >
                        <span v-if="isProcessing" class="animate-spin material-symbols-outlined">progress_activity</span>
                        <span>{{ isProcessing ? 'Splitting PDF...' : 'Split PDF' }}</span>
                        <span v-if="!isProcessing" class="material-symbols-outlined">arrow_forward</span>
                    </button>
                </div>
            </aside>
        </div>
    </div>

    <!-- Hidden Input -->
    <input type="file" ref="fileInput" accept="application/pdf" class="hidden" @change="onFileChange" />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, shallowRef, markRaw } from 'vue'
import { PDFDocument } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import JSZip from 'jszip'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const pages = ref([])
const pdfLibDoc = shallowRef(null)
const pdfjsDoc = shallowRef(null)
const currentFileName = ref('')
const isDragging = ref(false)
const isProcessing = ref(false)
const fileInput = ref(null)

// Navigation & Mode Tabs
const mainTab = ref('range') // 'range' | 'pages'
const rangeMode = ref('custom') // 'custom' | 'fixed'
const extractMode = ref('selected') // 'selected' | 'all'

// Options
const mergeAllRanges = ref(false)
const fixedPageSize = ref(1)
const customRanges = ref([
  { from: 1, to: 1 }
])

const selectedPageNumbers = computed(() => {
  return pages.value.filter(p => p.selected).map(p => p.pageNum)
})

// Compute Range page groups dynamically with REACTIVE page objects
const computedRanges = computed(() => {
  if (pages.value.length === 0) return []
  
  if (rangeMode.value === 'custom') {
    const list = []
    customRanges.value.forEach(cr => {
      const from = Math.max(1, Math.min(cr.from || 1, pages.value.length))
      const to = Math.max(from, Math.min(cr.to || from, pages.value.length))
      const pList = []
      for (let i = from; i <= to; i++) {
        const pObj = pages.value.find(x => x.pageNum === i)
        if (pObj) pList.push(pObj)
      }
      list.push({ from, to, pages: pList })
    })
    return list
  } else {
    // Fixed ranges
    const step = Math.max(1, fixedPageSize.value || 1)
    const list = []
    for (let i = 1; i <= pages.value.length; i += step) {
      const from = i
      const to = Math.min(i + step - 1, pages.value.length)
      const pList = []
      for (let k = from; k <= to; k++) {
        const pObj = pages.value.find(x => x.pageNum === k)
        if (pObj) pList.push(pObj)
      }
      list.push({ from, to, pages: pList })
    }
    return list
  }
})

const addCustomRange = () => {
  const last = customRanges.value[customRanges.value.length - 1]
  const nextFrom = last ? Math.min(last.to + 1, pages.value.length) : 1
  const nextTo = Math.min(nextFrom + 1, pages.value.length)
  customRanges.value.push({ from: nextFrom, to: nextTo })
}

const removeCustomRange = (idx) => {
  if (customRanges.value.length > 1) {
    customRanges.value.splice(idx, 1)
  }
}

const selectAllPages = () => pages.value.forEach(p => p.selected = true)
const deselectAllPages = () => pages.value.forEach(p => p.selected = false)

let uidCounter = 0

const renderPageThumbnail = async (pdfDoc, pageNum) => {
  try {
    const page = await pdfDoc.getPage(pageNum)
    const viewport = page.getViewport({ scale: 1.5, rotation: 0 })
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({ canvasContext: ctx, viewport }).promise
    return canvas.toDataURL("image/png")
  } catch (err) {
    console.error(`Page ${pageNum} render error:`, err)
    const fallbackCanvas = document.createElement("canvas")
    fallbackCanvas.width = 150
    fallbackCanvas.height = 200
    const ctx = fallbackCanvas.getContext("2d")
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, 150, 200)
    ctx.fillStyle = "#16304D"
    ctx.font = "bold 16px sans-serif"
    ctx.textAlign = "center"
    ctx.fillText(`Page ${pageNum}`, 75, 100)
    return fallbackCanvas.toDataURL("image/png")
  }
}

const processFile = async (file) => {
  if (file.type !== 'application/pdf') return
  currentFileName.value = file.name
  const buffer = await file.arrayBuffer()
  
  const loadedPdfLib = await PDFDocument.load(buffer)
  pdfLibDoc.value = markRaw(loadedPdfLib)
  
  try {
    const uint8 = new Uint8Array(buffer.slice(0))
    const loadedPdfjs = await pdfjsLib.getDocument({ data: uint8 }).promise
    pdfjsDoc.value = markRaw(loadedPdfjs)
  } catch (e) {
    console.error('PDFJS load error:', e)
  }

  const totalP = pdfjsDoc.value ? pdfjsDoc.value.numPages : pdfLibDoc.value.getPageCount()
  const newPages = []
  for (let i = 1; i <= totalP; i++) {
    newPages.push({
      uid: uidCounter++,
      pageNum: i,
      selected: true,
      thumbnail: '',
      isLoading: true
    })
  }

  pages.value = newPages

  // Default ranges Setup
  const half = Math.max(1, Math.floor(totalP / 2))
  customRanges.value = [
    { from: 1, to: half },
    { from: Math.min(half + 1, totalP), to: totalP }
  ]

  renderThumbnailsBackground()
}

const renderThumbnailsBackground = async () => {
  for (const pageObj of pages.value) {
    await new Promise(resolve => setTimeout(resolve, 30))
    if (pdfjsDoc.value) {
      pageObj.thumbnail = await renderPageThumbnail(pdfjsDoc.value, pageObj.pageNum)
    } else {
      const fallbackCanvas = document.createElement("canvas")
      fallbackCanvas.width = 150
      fallbackCanvas.height = 200
      const ctx = fallbackCanvas.getContext("2d")
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, 150, 200)
      ctx.fillStyle = "#16304D"
      ctx.font = "bold 16px sans-serif"
      ctx.textAlign = "center"
      ctx.fillText(`Page ${pageObj.pageNum}`, 75, 100)
      pageObj.thumbnail = fallbackCanvas.toDataURL("image/png")
    }
    pageObj.isLoading = false
  }
}

const processSplit = async () => {
  try {
    isProcessing.value = true
    await nextTick()

    if (mainTab.value === 'range') {
      if (mergeAllRanges.value) {
        // Merge all defined ranges into a single PDF
        const mergedPdf = await PDFDocument.create()
        for (const range of computedRanges.value) {
          const indices = range.pages.map(p => p.pageNum - 1)
          const copied = await mergedPdf.copyPages(pdfLibDoc.value, indices)
          copied.forEach(p => mergedPdf.addPage(p))
        }
        const bytes = await mergedPdf.save()
        downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `split-merged-${Date.now()}.pdf`)
      } else {
        // Output separate PDFs for each range
        if (computedRanges.value.length === 1) {
          const range = computedRanges.value[0]
          const singlePdf = await PDFDocument.create()
          const copied = await singlePdf.copyPages(pdfLibDoc.value, range.pages.map(p => p.pageNum - 1))
          copied.forEach(p => singlePdf.addPage(p))
          const bytes = await singlePdf.save()
          downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `range-${range.from}-${range.to}.pdf`)
        } else {
          const zip = new JSZip()
          for (let i = 0; i < computedRanges.value.length; i++) {
            const range = computedRanges.value[i]
            const rangePdf = await PDFDocument.create()
            const copied = await rangePdf.copyPages(pdfLibDoc.value, range.pages.map(p => p.pageNum - 1))
            copied.forEach(p => rangePdf.addPage(p))
            const bytes = await rangePdf.save()
            zip.file(`range-${i + 1}_pages-${range.from}-${range.to}.pdf`, bytes)
          }
          const content = await zip.generateAsync({ type: 'blob' })
          downloadBlob(content, `split-ranges-${Date.now()}.zip`)
        }
      }
    } else {
      // PAGES TAB
      if (extractMode.value === 'selected') {
        const pageNums = selectedPageNumbers.value
        if (pageNums.length === 0) return

        const extractedPdf = await PDFDocument.create()
        const copied = await extractedPdf.copyPages(pdfLibDoc.value, pageNums.map(p => p - 1))
        copied.forEach(p => extractedPdf.addPage(p))
        const bytes = await extractedPdf.save()
        downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `extracted-pages-${Date.now()}.pdf`)
      } else {
        // Extract all into ZIP
        const zip = new JSZip()
        for (const p of pages.value) {
          const singlePdf = await PDFDocument.create()
          const [copied] = await singlePdf.copyPages(pdfLibDoc.value, [p.pageNum - 1])
          singlePdf.addPage(copied)
          const bytes = await singlePdf.save()
          zip.file(`page-${p.pageNum}.pdf`, bytes)
        }
        const content = await zip.generateAsync({ type: 'blob' })
        downloadBlob(content, `all-pages-${Date.now()}.zip`)
      }
    }
  } catch (e) {
    console.error(e)
    alert('Error splitting PDF: ' + e.message)
  } finally {
    isProcessing.value = false
  }
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const onFileChange = (e) => {
  if (e.target.files.length) processFile(e.target.files[0])
  e.target.value = ''
}

const resetAll = () => {
  pages.value = []
  pdfLibDoc.value = null
  pdfjsDoc.value = null
  currentFileName.value = ''
}

const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; }
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; }
const onDragOver = (e) => { e.preventDefault(); }
const onDrop = (e) => {
  e.preventDefault()
  isDragging.value = false
  if (e.dataTransfer.files.length) processFile(e.dataTransfer.files[0])
}
</script>
